/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState, useEffect } from "react";
import {
  useTable,
  useExpanded,
  useGlobalFilter,
  useFilters,
  usePagination,
  useSortBy,
} from "react-table";
import formatNumber from "format-number";
import { __CommonGridData } from "../components/CommonGrid/constants";
import classes from "../components/CommonGrid/commonGrid.module.css";
import moment from "moment";

export const useCommonGrid = ({
  commonGridData = __CommonGridData,
  title = null,
  gridColumns = null,
  columnData = null,
  tHeight = "30rem",
  numberOfRows = 10,
  viewAllModal = true,
  hideColumn = false,
  filterOption,
  tableTotal = null,
  hiddenColumns = [],
  pagination,
  bulkUpdate,
  showTotal = false,
  defaultswitchBtn = null,
  defaultSearch = null,
  showLegends = false,
  reportLenders = null,
}) => {
  const [dataToPopulate, setDataToPopulate] = useState(
    columnData == null ? commonGridData.columnData : columnData
  );
  const [gridColumnDefinitions, setGridColumnDefinitions] = useState(
    gridColumns == null ? commonGridData.gridColumns : gridColumns
  );
  const [totalSection, setTotalSection] = useState(null);
  const [totalSectionArr, setTotalSectionArr] = useState([]);

  useEffect(() => {
    setDataToPopulate(
      columnData == null ? commonGridData.columnData : columnData
    );
    setGridColumnDefinitions(
      gridColumns == null ? commonGridData.gridColumns : gridColumns
    );
  }, [columnData]);
  const mappedColumnsData = gridColumnDefinitions?.map((data) =>
    data.title.toLowerCase().includes("date")
      ? {
          Header: data.title,
          accessor: data.field,
          maxWidth: 70,
          minWidth: 50,
          width: 60,
          sortType: (a, b) => {
            return (
              new Date(b.values[data.field]) - new Date(a.values[data.field])
            );
          },
          ...data,
        }
      : {
          Header: data.title,
          accessor: data.field,
          maxWidth: 70,
          minWidth: 50,
          width: 60,
          ...data,
        }
  );
  useEffect(() => {
    if (!tableTotal || !mappedColumnsData || !dataToPopulate) return;

    // Precompute keys object to avoid computing it inside loop
    const keys = mappedColumnsData.reduce((acc, cur) => {
      acc[cur.accessor] = "";
      return acc;
    }, {});

    // Compute the totals and store them in 'keys'
    tableTotal?.forEach((columnKey) => {
      const total = dataToPopulate.reduce(
        (sum, row) => sum + Number(row[columnKey] || 0),
        0
      );
      keys[columnKey] = formatNumber({ prefix: "$", round: 2 })(
        parseFloat(total).toFixed(5)
      );
    });

    // Generate the total section array
    const totalSectionArr = Object.values(keys);
    const totalIndex = totalSectionArr.findIndex((data) => data.length > 0) - 1;

    // Setting total section array with better condition handling
    setTotalSectionArr(
      totalSectionArr.map((data, index) => {
        if (index === 0) return "";
        if (index === totalIndex) return "Total";
        return data;
      })
    );

    // Creating and setting the total section elements
    setTotalSection(
      <>
        {totalSectionArr.map((data, index) => {
          if (index === 0) return <td key={index} className="total"></td>;
          if (index === totalIndex) return <td key={index}>Total</td>;
          return <td key={index}>{data}</td>;
        })}
      </>
    );
  }, [dataToPopulate]);
  // React Table
  const columns = useMemo(() => mappedColumnsData, []);
  const data = useMemo(() => dataToPopulate, [dataToPopulate]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    pageOptions,
    gotoPage,
    pageCount,
    prepareRow,
    state,
    allColumns,
    getToggleHideAllColumnsProps,
    // state: { expanded },
  } = useTable(
    {
      columns: columns,
      data: data,
      initialState: {
        pageSize: viewAllModal ? numberOfRows : data?.length,
        hiddenColumns: hiddenColumns,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    ...(viewAllModal ? [usePagination] : [])
  );
  const { pageIndex } = state;
  const otherKeys = filterOption?.reduce(
    (a, v, index) => ({
      ...a,
      [index]: {
        [v.type]: false,
        value: "",
        key: v.key,
        name: v.name,
        type: v.type,
        options: v?.options || [],
        apply: false,
      },
    }),
    {}
  );
  const [switchBtn, setSwitchBtn] = useState(defaultswitchBtn || otherKeys);
  const [searchValue, setSearchValue] = useState(defaultSearch);

  useEffect(() => {
    if (!defaultswitchBtn) return;

    let filteredData = [...columnData];
    filterOption?.forEach(({ type, key }, index) => {
      const value = defaultswitchBtn[index]?.value;
      if (!value) return;

      switch (type) {
        case "date":
          filteredData = filteredData.filter(
            (item) =>
              moment(item[key], "MM-DD-YYYY").format("YYYY-MM-DD") === value
          );
          break;
        case "month":
          const monthsAgo = moment().subtract(value, "months");
          filteredData = filteredData.filter(
            (item) => moment(item[key], "MM-DD-YYYY") >= monthsAgo
          );
          break;
        case "range":
          filteredData = filteredData.filter(
            (item) => value.min <= item[key] && item[key] <= value.max
          );
          break;
        case "select":
          if (value !== "all") {
            filteredData = filteredData.filter((item) => item[key] === value);
          }
          break;
        default:
          break;
      }
    });
    setDataToPopulate(filteredData);
  }, []);
  useEffect(() => {
    if (!defaultSearch) {
      setDataToPopulate(columnData);
      return;
    }

    const lowercaseQuery = defaultSearch.toLowerCase();
    const rowMatches = (row) =>
      Object.values(row).some((value) => {
        if (typeof value === "number") {
          return (
            value.toString().includes(lowercaseQuery) ||
            formatNumber({ prefix: "$", round: 2 })(value.toFixed(5))
              .toString()
              .includes(lowercaseQuery)
          );
        } else if (typeof value === "string") {
          return value.toLowerCase().includes(lowercaseQuery);
        } else if (
          value &&
          typeof value === "object" &&
          value.props &&
          typeof value.props.children === "string"
        ) {
          return value.props.children.toLowerCase().includes(lowercaseQuery);
        }
        return false;
      });

    const filteredData = columnData.flatMap((item) =>
      item.subRows && item.subRows.length
        ? item.subRows.filter(rowMatches)
        : rowMatches(item)
        ? [item]
        : []
    );

    setDataToPopulate(filteredData);
  }, []);

  return {
    state: state,
    title: title,
    rows: rows,
    headerGroups: headerGroups,
    dataToPopulate: dataToPopulate,
    preGlobalFilteredRows: columnData || commonGridData.columnData,
    setDataToPopulate: setDataToPopulate,
    filterOption: filterOption,
    allColumns: allColumns,
    getToggleHideAllColumnsProps: getToggleHideAllColumnsProps,
    hideColumn: hideColumn,
    gridColumns: gridColumns,
    columnData: columnData,
    viewAllModal: viewAllModal,
    totalSection: totalSection,
    totalSectionArr: totalSectionArr,
    bulkUpdate: bulkUpdate,
    getTableProps: getTableProps,
    classes: classes,
    tHeight: tHeight,
    getTableBodyProps: getTableBodyProps,
    page: viewAllModal ? page : rows,
    prepareRow: prepareRow,
    showTotal: showTotal,
    pagesCount: pageCount,
    currentPage: pageIndex + 1,
    setCurrentPage: gotoPage,
    pageIndex: pageIndex,
    pageOptions: pageOptions,
    pagination: pagination,
    numberOfRows: numberOfRows,
    tableTotal: tableTotal,
    switchBtn,
    setSwitchBtn,
    otherKeys,
    defaultswitchBtn,
    searchValue,
    setSearchValue,
    defaultSearch,
    showLegends: showLegends,
    reportLenders: reportLenders,
  };
};
